import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/shared";
import { Combobox } from "@/components/shared/Combobox";
import { COUNTRIES } from "@/lib/countries";
import { ClientDialog } from "@/components/clients/ClientDialog";

const blank = () => ({ name: "", email: "", client_id: "", country: "" });

export const AddPersonDialog = ({ open, onOpenChange, clients, onSaved, onClientCreated }) => {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [newCo, setNewCo] = useState(null);
  useEffect(() => { if (open) setF(blank()); }, [open]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const valid = f.name.trim() && f.email.trim() && f.client_id;
  const submit = async (e) => {
    e.preventDefault(); if (!valid) return; setBusy(true);
    try {
      const { data } = await api.post(`/clients/${f.client_id}/contacts`, { name: f.name, email: f.email, country: f.country || "" });
      const co = clients.find((c) => c.id === f.client_id)?.company_name || "";
      toast[data.existing ? "info" : "success"](<span>{data.existing ? `${data.email} already exists under ${co} — opened the existing record` : `${data.name} added to ${co}`} · <a href={`/contacts/${data.id}`} className="underline">Open profile</a></span>);
      onSaved?.(data); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (<>
    <Dialog open={open && !newCo} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-person-dialog">
        <DialogHeader><DialogTitle>Add Person</DialogTitle><DialogDescription>Add an individual and link them to their existing company. Companies are added separately.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Full name" required><Input value={f.name} onChange={set("name")} className="h-9" data-testid="person-name" autoFocus /></Field>
          <Field label="Official email" required><Input type="email" value={f.email} onChange={set("email")} className="h-9" data-testid="person-email" /></Field>
          <Field label="Company" required hint="Not listed? Use “Add company” in the dropdown — you'll come back here with it selected."><Combobox testid="person-company" items={clients.map((c) => ({ value: c.id, label: c.company_name }))} value={f.client_id} onChange={set("client_id")} placeholder="Search client companies…" addLabel="Add company" onAdd={(q) => setNewCo(q || "")} /></Field>
          <Field label="Country" hint="Where this person is based — does not change the company's record or currency"><Combobox testid="person-country" items={COUNTRIES.map((c) => ({ value: c, label: c }))} value={f.country} onChange={set("country")} placeholder="Search countries…" /></Field>
          <div className="flex justify-end gap-2 pt-1"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy || !valid} data-testid="person-save">{busy ? "Saving…" : "Add person"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
    {newCo !== null && <ClientDialog open onOpenChange={(v) => { if (!v) setNewCo(null); }} initialName={newCo} onSaved={(c) => { onClientCreated?.(c); setF((x) => ({ ...x, client_id: c.id })); setNewCo(null); }} />}
  </>);
};
